import {
  DeleteProduct,
  GetProduct,
  GetProductCategories,
  GetProducts,
  PostProductCategory,
  PostProducts,
  PutProducts,
  GetProductCategory,
  PutProductCategory,
  DeleteProductCategory,
  GetProductStocks,
  PostProductInternalUse,
  DeleteProductInternalUseStockMovement,
  PostProductWriteOff,
  DeleteProductWriteOffStockMovement,
  GetProductMaterialCard,
  GetProductStockExportFifo,
  GetProductImages,
  GetProductImageUploadUrl,
  PostCompleteProductImageUpload,
  DeleteProductImage,
  PutProductImagesOrder,
} from "@/server-types";
import { useCustomQuery } from "../custom-query";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "..";

export const useGetProducts = (
  query: GetProducts["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["getProducts", query],
    queryFn: () => get<GetProducts>("/product", query as GetProducts["query"]),
    enabled: query.enabled ?? true,
  });
};

export const useGetProductCategories = (
  query: GetProductCategories["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["getProductCategories", query],
    queryFn: () => get<GetProductCategories>("/product/category", query),
    enabled: query.enabled ?? true,
  });
};

export const useGetProductCategoryById = (id: number) => {
  return useCustomQuery({
    queryKey: ["getProductCategoryById", id],
    queryFn: () => get<GetProductCategory>(`/product/category/${id}`),
    enabled: id !== -1,
    retry: false,
  });
};

export const usePostProduct = () => {
  return useCustomMutation({
    mutationFn: (body: PostProducts["body"]) =>
      post<PostProducts>("/product", body),
  });
};

export const useGetProductById = (id: number) => {
  return useCustomQuery({
    queryKey: ["getProductById", id],
    queryFn: () => get<GetProduct>(`/product/${id}`),
    enabled: id !== -1,
    retry: false,
  });
};

export const usePutProduct = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      productId,
    }: {
      body: PutProducts["body"];
      productId: number;
    }) => put<PutProducts>(`/product/${productId}`, body),
    displayNotification: true,
  });
};

export const usePostProductCategory = () => {
  return useCustomMutation({
    mutationFn: (body: PostProductCategory["body"]) =>
      post<PostProductCategory>("/product/category", body),
  });
};

export const useDeleteProductById = () => {
  return useCustomMutation({
    mutationFn: (id: number) => del<DeleteProduct>(`/product/${id}`),
    displayNotification: true,
  });
};

export const usePutProductCategory = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      categoryId,
    }: {
      body: PutProductCategory["body"];
      categoryId: number;
    }) => put<PutProductCategory>(`/product/category/${categoryId}`, body),
    displayNotification: true,
  });
};

export const useDeleteProductCategoryById = () => {
  return useCustomMutation({
    mutationFn: (id: number) =>
      del<DeleteProductCategory>(`/product/category/${id}`),
    displayNotification: true,
  });
};

// product stocks

export const useGetProductStocks = (
  query: Omit<GetProductStocks["query"], "locationId"> & {
    locationId?: number;
  },
) => {
  return useCustomQuery({
    queryKey: ["getProductStocks", query],
    queryFn: () =>
      get<GetProductStocks>(
        "/product/stock",
        query as GetProductStocks["query"],
      ),
    enabled: !!query.locationId || !!query.productId,
  });
};

export const usePostProductInternalUse = () => {
  return useCustomMutation({
    mutationFn: (body: PostProductInternalUse["body"]) =>
      post<PostProductInternalUse>("/product/stock/internal-use", body),
  });
};

export const useDeleteProductInternalUseStockMovement = () => {
  return useCustomMutation({
    mutationFn: (id: number) =>
      del<DeleteProductInternalUseStockMovement>(
        `/product/stock/internal-use/${id}`,
      ),
  });
};

export const usePostProductWriteOff = () => {
  return useCustomMutation({
    mutationFn: (body: PostProductWriteOff["body"]) =>
      post<PostProductWriteOff>("/product/stock/write-off", body),
  });
};

export const useDeleteProductWriteOffStockMovement = () => {
  return useCustomMutation({
    mutationFn: (id: number) =>
      del<DeleteProductWriteOffStockMovement>(`/product/stock/write-off/${id}`),
  });
};

export const useInfiniteGetProducts = (
  query: Omit<GetProducts["query"], "locationId"> & {
    locationId?: number;
    businessPremiseId?: string;
  },
) => {
  return useInfiniteQuery({
    queryKey: ["getProductsInfinite", query],
    queryFn: async ({ pageParam = 0 }) => {
      return get<GetProducts>("/product", {
        ...query,
        offset: pageParam,
        limit: query.limit ?? 25,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  });
};

export const useGetProductMaterialCard = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      productId,
    }: {
      body: GetProductMaterialCard["body"];
      productId: number;
    }) =>
      post<GetProductMaterialCard>(`/product/${productId}/material-card`, body),
  });
};

export const useGetProductStockExportFifo = () => {
  return useCustomMutation({
    mutationFn: (body: GetProductStockExportFifo["body"]) =>
      post<GetProductStockExportFifo>(
        "/product/stock/export/fifo",
        body as GetProductStockExportFifo["body"],
      ),
  });
};

/**
 * GET /product/:productId/images
 */
export const useGetProductImages = (id: number) => {
  return useCustomQuery({
    queryKey: ["productImages", id],
    queryFn: () => get<GetProductImages>(`/product/${id}/images`),
    enabled: id > 0,
  });
};

/**
 * GET /product/:productId/images/upload
 */
export const useGetProductImageUploadUrl = () => {
  return useCustomMutation({
    mutationKey: ["getProductImageUploadUrl"],
    mutationFn: ({
      productId,
      query,
    }: {
      productId: number;
      query: GetProductImageUploadUrl["query"];
    }) =>
      get<GetProductImageUploadUrl>(
        `/product/${productId}/images/upload`,
        query,
      ),
  });
};

/**
 * POST /product/:productId/images/upload/complete
 */
export const useCompleteProductImageUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeProductImageUpload"],
    mutationFn: ({
      productId,
      body,
    }: {
      productId: number;
      body: PostCompleteProductImageUpload["body"];
    }) =>
      post<PostCompleteProductImageUpload>(
        `/product/${productId}/images/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["productImages", variables.productId],
      });
    },
  });
};

/**
 * PUT /product/:productId/images/order
 */
export const usePutProductImagesOrder = () => {
  return useCustomMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: number;
      body: PutProductImagesOrder["body"];
    }) =>
      put<PutProductImagesOrder>(`/product/${productId}/images/order`, body),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["productImages", variables.productId],
      });
    },
  });
};

/**
 * DELETE /product/:productId/images/:imageId
 */
export const useDeleteProductImage = () => {
  return useCustomMutation({
    mutationFn: ({
      productId,
      imageId,
    }: {
      productId: number;
      imageId: number;
    }) => del<DeleteProductImage>(`/product/${productId}/images/${imageId}`),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["productImages", variables.productId],
      });
    },
  });
};
